'use client'

import { useEffect, useState } from 'react'
import { Button } from '@vinted/web-ui'

import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { reloadPage } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  clickEvent,
  ComplaintResolutionAction,
  resolveComplaint,
  ComplaintModel,
} from '@marketplace-web/complaints/complaints-data'
import { useSession } from '@marketplace-web/shared/session-data'

import ResolveIssueConfirmationModal from './ResolveIssueConfirmationModal'

type Props = {
  shouldOpen?: boolean
  complaint?: ComplaintModel | null
  url?: string
  isChooseResolutionFlow?: boolean
  theme?: React.ComponentProps<typeof Button>['theme']
}

const ResolveIssueButton = ({
  shouldOpen,
  complaint,
  url,
  isChooseResolutionFlow,
  theme,
}: Props) => {
  const translate = useTranslate('resolve_issue.modal')
  const { track } = useTracking()
  const { safeBack } = useBrowserNavigation()
  const { user } = useSession()

  const isBuyer = complaint?.buyerId === user?.id

  const screen = isChooseResolutionFlow ? 'complaint_resolution' : 'complaint_proof'

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [errors, setErrors] = useState<Array<ErrorItem>>([])

  useEffect(() => {
    if (!shouldOpen) return

    setIsModalOpen(true)
  }, [shouldOpen])

  function handleButtonClick() {
    setIsModalOpen(true)
  }

  function handleModalClose() {
    setIsModalOpen(false)
  }

  function trackSubmit() {
    if (!complaint) return

    const { transactionId, id } = complaint

    const targetDetails = {
      transaction_id: transactionId,
      complaint_id: id,
    }

    track(
      clickEvent({
        screen,
        target: 'confirm_submit',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  async function handleModalSubmit() {
    if (!complaint) return

    trackSubmit()

    const response = await resolveComplaint(complaint.id.toString())

    if ('errors' in response) {
      setErrors(response.errors)

      return
    }

    if (url) {
      safeBack(url)

      return
    }

    reloadPage()
  }

  const resolutionAction = complaint?.resolutions?.find(
    resolution => resolution.action === ComplaintResolutionAction.Refund,
  )

  if (!resolutionAction && !isChooseResolutionFlow) return null

  return (
    <>
      {!isChooseResolutionFlow && (
        <Button
          text={resolutionAction?.button}
          size={Button.Size.Medium}
          onClick={handleButtonClick}
          theme={isBuyer ? theme : undefined}
          testId="resolve-issue-button"
        />
      )}
      <ResolveIssueConfirmationModal
        isOpen={isModalOpen}
        title={isChooseResolutionFlow ? translate('title') : resolutionAction?.confirmTitle}
        bodyText={isChooseResolutionFlow ? translate('body') : resolutionAction?.confirmation}
        confirmText={
          isChooseResolutionFlow ? translate('actions.confirm') : resolutionAction?.confirmButton
        }
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        validationError={errors}
      />
    </>
  )
}

export default ResolveIssueButton
